import {
	AttachmentFile,
	Currency,
	Dtos,
	formatToDollar,
	WipAttachmentFileType,
	WorkInProgressStatementId,
	wipAttachmentTypeLabelMap,
} from "@inrev/common";
import type { AdminWipStatement } from "@portal/api/account/admin";
import { adminWipStatementAttachmentsQueryKey } from "@portal/api/financial-statement";
import {
	useAdminDeleteWorkInProgressStatement,
	useAdminGetWipStatementAttachments,
	useAdminLockWipStatement,
	useAdminUnlockWipStatement,
	useAdminUpsertWorkInProgressStatement,
} from "@portal/api/financial-statement/admin";
import { FormField, FormItemLabel } from "@portal/components/form";
import { FormCurrencyLineItem } from "@portal/components/form/fieldLayouts";
import { FormDatePicker } from "@portal/components/form/fields";
import { AttachmentSection } from "@portal/components/layout/AttachmentSection";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { Modal } from "@portal/components/layout/Modal";
import { ModalContentWithScrollStatusMenu } from "@portal/components/layout/ModalContentWithScrollStatusMenu";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { Button, Card, ClickableIcon, Icon, Text } from "@portal/components/primitives";
import { LineItem } from "@portal/components/ui/LineItem";
import { LockButton } from "@portal/components/ui/LockButton";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { formatEmpty, formatFromISODate } from "../../../../../utils";
import { stripEmptyResolver, typedZodResolver } from "../../../../../utils/form";
import type { AccountIdentity } from "../../../../shared/account/AccountHeader";
import { SectionHeaderWithAdd } from "../details/SectionHeaderWithAdd";
import { StatementDateSelector } from "./StatementDateSelector";

export const AdminAccountWIPSection = ({
	account,
	wipStatements,
}: {
	account: Pick<AccountIdentity, "id" | "primaryCompanyId">;
	wipStatements: AdminWipStatement[];
}) => {
	const { deleteWipStatement } = useAdminDeleteWorkInProgressStatement(account.id);
	const { lockWipStatement } = useAdminLockWipStatement(account.id);
	const { unlockWipStatement } = useAdminUnlockWipStatement(account.id);

	const [currentWipStatementIndex, setCurrentWipStatementIndex] = useState<number | undefined>(
		undefined,
	);
	const [wipStatementEditorOpen, setWipStatementEditorOpen] = useState<boolean>(false);

	const sortedWipStatements = useMemo(
		() => [...wipStatements].sort((a, b) => b.date.localeCompare(a.date)),
		[wipStatements],
	);

	const currentWipStatement: AdminWipStatement | undefined = useMemo(() => {
		if (currentWipStatementIndex !== undefined) {
			return sortedWipStatements[currentWipStatementIndex];
		}
	}, [sortedWipStatements, currentWipStatementIndex]);

	useEffect(() => {
		wipStatements.length ? setCurrentWipStatementIndex(0) : setCurrentWipStatementIndex(undefined);
	}, [wipStatements]);

	const hasStatements = sortedWipStatements.length > 0;
	const openEditor = () => setWipStatementEditorOpen(true);

	return (
		<>
			{hasStatements ? (
				<SectionHeaderWithAdd
					title="Work In Progress"
					testId="admin-add-wip-statement-button"
					addLabel="Add Work In Progress Statement"
					onAdd={openEditor}
				/>
			) : (
				<Text type="heading" color="primary">
					Work In Progress
				</Text>
			)}
			{hasStatements ? (
				<div
					data-testid="admin-wip-statement-selector"
					className="flex items-center flex-wrap gap-[10px]"
				>
					{sortedWipStatements.map((statement, index) => (
						<StatementDateSelector
							key={statement.id}
							onClick={() => setCurrentWipStatementIndex(index)}
							date={statement.date}
							isCurrentStatement={index === currentWipStatementIndex}
						/>
					))}
				</div>
			) : (
				<Button
					color="primary"
					className="w-fit"
					data-testid="admin-add-wip-statement-button"
					leadingIcon={<Icon type="plus" size="mini" color="inverse" />}
					onClick={openEditor}
				>
					Add WIP Statement
				</Button>
			)}
			{currentWipStatement !== undefined && (
				<_WipDisplay
					account={account}
					currentWipStatement={currentWipStatement}
					deleteWipStatement={deleteWipStatement}
					lockWipStatement={lockWipStatement}
					unlockWipStatement={unlockWipStatement}
				/>
			)}
			{wipStatementEditorOpen && (
				<_WipEditModal
					account={account}
					currentWipStatement={undefined}
					onClose={() => setWipStatementEditorOpen(false)}
				/>
			)}
		</>
	);
};

const _WipDisplay = ({
	account,
	currentWipStatement,
	deleteWipStatement,
	lockWipStatement,
	unlockWipStatement,
}: {
	account: Pick<AccountIdentity, "id" | "primaryCompanyId">;
	currentWipStatement: AdminWipStatement;
	deleteWipStatement: (value: any) => void;
	lockWipStatement: (value: WorkInProgressStatementId) => void;
	unlockWipStatement: (value: WorkInProgressStatementId) => void;
}) => {
	const [selectedWipStatementId, setSelectedWipStatementId] = useState<
		WorkInProgressStatementId | undefined
	>(undefined);

	return (
		<div data-testid="admin-wip-statement-display" className="flex flex-col space-y-6 flex-1">
			<Card
				elevation="raised"
				className="rounded-xl"
				data-testid="admin-account-wip-statement-card"
			>
				<Card.Body className="flex w-full flex-col p-6 space-y-5">
					<div className="flex items-center justify-between w-full">
						<Text type="title" color="primary" className="flex-1">
							{formatFromISODate(currentWipStatement.date)}
						</Text>
						<div className="flex items-right space-x-[8px] w-fit h-fit">
							<LockButton
								locked={currentWipStatement.underwriterLock}
								onClick={() => {
									if (currentWipStatement !== undefined) {
										if (currentWipStatement.underwriterLock) {
											unlockWipStatement(currentWipStatement.id);
										} else {
											lockWipStatement(currentWipStatement.id);
										}
									}
								}}
							/>
							<ClickableIcon
								type="pencil-square"
								size="small"
								color="secondary"
								aria-label="Edit work in progress statement"
								onClick={() => {
									if (currentWipStatement !== undefined) {
										setSelectedWipStatementId(currentWipStatement.id);
									}
								}}
							/>

							<ConfirmationModalClickWrapper
								message={
									<div className="w-[375px] text-center break-words">
										Are you sure you want to delete this financial statement?
									</div>
								}
								confirmButtonText="Delete"
								className="items-center w-fit h-fit"
								confirmationButtonColor="danger"
								onConfirm={() => {
									if (currentWipStatement !== undefined) {
										deleteWipStatement(currentWipStatement.id);
									}
								}}
							>
								<ClickableIcon
									type="trash"
									size="small"
									color="danger"
									aria-label="Delete work in progress statement"
								/>
							</ConfirmationModalClickWrapper>
						</div>
					</div>
					<div className="space-y-3">
						<LineItem label="Backlog (CTC)">
							{formatEmpty(formatToDollar(currentWipStatement?.projectBacklogCost))}
						</LineItem>
						<LineItem label="Gross Profit In Backlog">
							{formatEmpty(formatToDollar(currentWipStatement?.projectBacklogGrossProfit))}
						</LineItem>
					</div>
					<_WipStatementAttachmentSection
						account={account}
						currentWipStatement={currentWipStatement}
						header="Statement Attachments"
					/>
				</Card.Body>
			</Card>
			{selectedWipStatementId && (
				<_WipEditModal
					account={account}
					currentWipStatement={currentWipStatement}
					onClose={() => setSelectedWipStatementId(undefined)}
				/>
			)}
		</div>
	);
};

const _WipStatementAttachmentSection = ({
	account,
	currentWipStatement,
	header = "",
}: {
	account: Pick<AccountIdentity, "id">;
	currentWipStatement: AdminWipStatement;
	header?: string;
}) => {
	const { wipStatementAttachments } = useAdminGetWipStatementAttachments(currentWipStatement.id);
	const [attachments, setAttachments] = useState<AttachmentFile<WipAttachmentFileType>[]>(
		wipStatementAttachments || [],
	);
	useEffect(() => {
		setAttachments(wipStatementAttachments ?? []);
	}, [currentWipStatement, wipStatementAttachments]);

	if (!currentWipStatement) return null;

	return (
		<div
			data-testid="admin-wip-statement-attachments"
			className="flex flex-col space-y-[15px] flex-1"
		>
			{!header && (
				<Text type="caption" color="tertiary" className="italic mb-[-15px]">
					Work In Progress Statement Attachments
				</Text>
			)}
			<AttachmentSection
				expandedByDefault={true}
				allowTypeConfirmation={true}
				showTabs={false}
				allowDelete={true}
				downloadAllName={`Work In Progress Statement Attachments`}
				header={header ?? "Work In Progress Statement Attachments"}
				attachments={attachments}
				upload={{
					url: `/v2/admin/surety/accounts/${account.id}/work-in-progress-statements/${currentWipStatement.id}/attachments`,
					onChange: (val) => setAttachments(val),
					allowedTypesAndLabels: wipAttachmentTypeLabelMap,
					invalidateQueryKeys: [adminWipStatementAttachmentsQueryKey(currentWipStatement.id)],
					analyticsContext: "wip-statements",
					trackingPrefix: "account-admin-wip-attachments",
				}}
				download={{
					baseUrl: `/v2/admin/surety/wip-statement/${currentWipStatement.id}/attachments`,
					baseQueryKey: "adminAccountAttachments",
				}}
			/>
		</div>
	);
};

export type DefaultWipFormData = {
	date: string;
	companyContactId: string;
	projectBacklogCost?: Currency | string;
	projectBacklogGrossProfit?: Currency | string;
};

const getDefaultFormData = (companyContactId: string): DefaultWipFormData => ({
	date: "",
	companyContactId,
	projectBacklogCost: "",
	projectBacklogGrossProfit: "",
});

const selectedToFormData = (selected: AdminWipStatement): DefaultWipFormData => ({
	date: selected.date,
	companyContactId: selected.companyContactId,
	projectBacklogCost: selected.projectBacklogCost ?? "",
	projectBacklogGrossProfit: selected.projectBacklogGrossProfit ?? "",
});

const _WipEditModal = ({
	account,
	currentWipStatement,
	onClose,
}: {
	account: Pick<AccountIdentity, "id" | "primaryCompanyId">;
	currentWipStatement: AdminWipStatement | undefined;
	onClose: () => void;
}) => {
	const { upsertWipStatement, upsertWipStatementIsLoading, upsertWipStatementSuccess } =
		useAdminUpsertWorkInProgressStatement(account.id);

	const defaultFormData =
		currentWipStatement !== undefined
			? selectedToFormData(currentWipStatement)
			: getDefaultFormData(account.primaryCompanyId);
	const formMethods = useForm<DefaultWipFormData, any, Dtos.Admin.WipStatement.Create.Request>({
		defaultValues: defaultFormData,
		resolver: stripEmptyResolver(typedZodResolver(Dtos.Admin.WipStatement.Create.Request.schema)),
	});

	const handleSubmit = (formData: Dtos.Admin.WipStatement.Create.Request) => {
		upsertWipStatement({
			data: formData,
			...(currentWipStatement?.id && { workInProgressId: currentWipStatement.id }),
		});
	};
	useEffect(() => {
		if (upsertWipStatementSuccess) {
			onClose();
		}
	}, [upsertWipStatementSuccess, onClose]);

	return (
		<_StatementModalLayout
			onClose={onClose}
			onSubmit={formMethods.handleSubmit(handleSubmit)}
			form={
				<form
					onSubmit={formMethods.handleSubmit(handleSubmit)}
					className="w-full min-h-fit flex-1 flex flex-col py-[35px] px-[45px] space-y-[18px]"
				>
					<div data-testid="admin-wip-statement-form" className="flex flex-col space-y-[12px]">
						<FormField className="flex flex-row items-center justify-between shrink-0" id="date">
							<FormItemLabel required className="max-w-fit">
								Statement Date
							</FormItemLabel>
							<FormDatePicker
								name="date"
								control={formMethods.control}
								defaultMonth={DateTime.now().minus({ years: 1 }).endOf("year")}
								disableAfter={DateTime.now().toISODate()}
								className="w-[220px]"
							/>
						</FormField>
						<FormCurrencyLineItem
							label="Backlog (CTC)"
							name="projectBacklogCost"
							formMethods={formMethods}
							id="projectBacklogCost"
							labelClassName="text-tertiary"
						/>
						<FormCurrencyLineItem
							label="Gross Profit In Backlog"
							name="projectBacklogGrossProfit"
							formMethods={formMethods}
							id="projectBacklogGrossProfit"
							labelClassName="text-tertiary"
						/>
					</div>
				</form>
			}
			header={
				currentWipStatement ? "Edit Work In Progress Statement" : "Add Work In Progress Statement"
			}
			isLoading={upsertWipStatementIsLoading}
		/>
	);
};

const _StatementModalLayout = ({
	onClose,
	onSubmit,
	form,
	header,
	isLoading,
}: {
	onClose: () => void;
	onSubmit: () => void;
	header: React.ReactNode;
	form: React.ReactNode;
	isLoading: boolean;
}) => {
	return (
		<Modal onClickOutside={onClose}>
			<ModalItemWithHeader
				header={
					<Text type="title" color="primary">
						{header}
					</Text>
				}
				onClose={onClose}
				bodyClassName="w-full overflow-y-auto"
				className="h-fit overflow-hidden"
			>
				<ModalContentWithScrollStatusMenu
					buttons={
						<div className="w-full flex justify-center mt-[5px]">
							<Button
								onClick={onSubmit}
								color="primary"
								className="w-[150px]"
								tabIndex={2}
								loading={isLoading}
							>
								Submit
							</Button>
						</div>
					}
				>
					{form}
				</ModalContentWithScrollStatusMenu>
			</ModalItemWithHeader>
		</Modal>
	);
};
